import { Card, CardContent } from '@/components/ui/card';
import { ArrowUpRight, ArrowDownRight } from 'lucide-react';

/**
 * Tarjeta KPI estándar para dashboards v2.
 *
 * Props:
 *  - icon: lucide-react component
 *  - label: string
 *  - value: string | number
 *  - sub?: string (descripción debajo del valor)
 *  - delta?: { value: number, label?: string } (cambio vs periodo anterior)
 *  - color?: 'cyan' | 'emerald' | 'amber' | 'violet' | 'rose' | 'sky'
 *  - loading?: bool
 *  - testid?: string
 */
const COLOR_MAP = {
  cyan: { text: 'text-cyan-400', bg: 'bg-cyan-500/10', border: 'border-cyan-500/20' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/20' },
  amber: { text: 'text-amber-400', bg: 'bg-amber-500/10', border: 'border-amber-500/20' },
  violet: { text: 'text-violet-400', bg: 'bg-violet-500/10', border: 'border-violet-500/20' },
  rose: { text: 'text-rose-400', bg: 'bg-rose-500/10', border: 'border-rose-500/20' },
  sky: { text: 'text-sky-400', bg: 'bg-sky-500/10', border: 'border-sky-500/20' },
};

export default function KpiCard({
  icon: Icon,
  label,
  value,
  sub,
  delta,
  color = 'cyan',
  loading = false,
  testid,
}) {
  const palette = COLOR_MAP[color] || COLOR_MAP.cyan;
  const deltaPositive = (delta?.value ?? 0) >= 0;

  return (
    <Card className={`bg-card/50 backdrop-blur-md ${palette.border} hover:border-white/10 transition-colors w-[128px] shrink-0 sm:w-auto sm:shrink`} data-testid={testid || `kpi-${label}`}>
      <CardContent className="p-2 sm:p-2.5">
        <div className="flex items-center justify-between mb-0.5">
          <div className={`w-6 h-6 rounded-md ${palette.bg} flex items-center justify-center shrink-0`}>
            {Icon && <Icon className={`w-3.5 h-3.5 ${palette.text}`} />}
          </div>
          {delta != null && (
            <span className={`flex items-center gap-0.5 text-[9px] font-semibold ${deltaPositive ? 'text-emerald-400' : 'text-rose-400'}`}>
              {deltaPositive ? <ArrowUpRight className="w-2.5 h-2.5" /> : <ArrowDownRight className="w-2.5 h-2.5" />}
              {Math.abs(delta.value)}%
            </span>
          )}
        </div>
        {loading ? (
          <div className="h-5 w-14 bg-white/5 rounded animate-pulse" />
        ) : (
          <p className={`text-lg font-bold ${palette.text} font-heading leading-none truncate`}>{value}</p>
        )}
        <p className="text-[9px] text-muted-foreground mt-0.5 uppercase tracking-wider truncate">{label}</p>
        {sub && <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}
